import React, { useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  alpha,
  Box,
  Chip,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  totalValue?: number;
  totalLabel?: string;
  color?: string;
  icon?: React.ReactNode;
  sx?: any;
}

export const CollapsibleSection = ({
  title,
  children,
  defaultExpanded = true,
  totalValue,
  totalLabel,
  color = '#ffffff',
  icon,
  sx = {},
}: CollapsibleSectionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box sx={{ mb: { xs: 3, sm: 4 }, ...sx }}>
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          p: { xs: 1.5, sm: 2 },
          background: expanded
            ? `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(
                color,
                0.05
              )} 100%)`
            : 'transparent',
          borderRadius: 2,
          border: `1px solid ${alpha(color, 0.2)}`,
          transition: 'all 0.3s ease',
          gap: { xs: 1, sm: 2 },
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(
              color,
              0.15
            )} 0%, ${alpha(color, 0.08)} 100%)`,
            borderColor: alpha(color, 0.3),
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: { xs: '0 1 auto', sm: 1 },
            minWidth: 0,
            overflow: 'hidden',
            mr: { xs: 1, sm: 2 },
          }}
        >
          {icon && (
            <Box
              sx={{
                mr: { xs: 1, sm: 1.5 },
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="h6"
            fontWeight={700}
            color={color}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: { xs: '0 1 auto', sm: 1 },
              minWidth: 0,
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              '&::before': {
                content: '""',
                width: 4,
                height: 20,
                bgcolor: color,
                borderRadius: 2,
                mr: { xs: 1, sm: 2 },
                flexShrink: 0,
              },
            }}
            noWrap
          >
            {title}
          </Typography>
          {totalValue !== undefined && totalLabel && (
            <Chip
              label={`${totalLabel}: $${totalValue.toLocaleString()}`}
              size="small"
              sx={{
                ml: { xs: 1, sm: 2 },
                bgcolor: alpha(color, 0.2),
                color: color,
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.813rem' },
                flexShrink: 0,
                height: { xs: 24, sm: 28 },
                '& .MuiChip-label': {
                  overflow: 'visible',
                  textOverflow: 'clip',
                  whiteSpace: 'nowrap',
                  px: { xs: 0.75, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                },
                // Ensure value is always visible - prioritize showing full value
                maxWidth: { xs: 'none', sm: 'none', md: 'none' },
                minWidth: 'fit-content',
              }}
            />
          )}
        </Box>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          sx={{
            color: color,
            '&:hover': {
              bgcolor: alpha(color, 0.1),
            },
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};
